import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useFacebookToken } from '@/hooks/useFacebookToken';
import { Loader2 } from 'lucide-react';
import { ReactNode } from 'react';
import { toast } from 'sonner';
import Icons from './Icons';

type SocialConnectProps = {
  isOpen?: boolean;
  onClose?: () => void;
  children: ReactNode;
};

export const SocialConnectDialog = ({
  isOpen,
  onClose,
  children,
}: SocialConnectProps) => {
  const { authenticate } = useFacebookToken();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Connect Socials</DialogTitle>
          <DialogDescription>
            Connect your social accounts to get started.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="flex items-center justify-between space-x-4 p-4 border rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-6 h-6">
                <Icons.instagram />
              </div>
              <div>
                <p className="text-sm font-medium leading-none">Instagram</p>
              </div>
            </div>
            <Button
              variant="outline"
              disabled={authenticate.isPending}
              onClick={async () => {
                try {
                  await authenticate.mutateAsync();

                  toast.success('Instagram connected');

                  setTimeout(() => {
                    window.location.reload();
                  }, 1000);
                } catch (error) {
                  console.error(error);
                  if (
                    error &&
                    typeof error === 'object' &&
                    'message' in error &&
                    error.message === 'No accounts found'
                  ) {
                    toast.error(
                      'No Instagram accounts found, please create a page and try again.',
                    );
                  } else {
                    toast.error('Failed to connect Instagram');
                  }
                }
              }}>
              {authenticate.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Connecting...
                </>
              ) : (
                'Connect'
              )}
            </Button>
          </div>
          <div className="flex items-center justify-between space-x-4 p-4 border rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-6 h-6">
                <Icons.tiktok />
              </div>
              <div>
                <p className="text-sm font-medium leading-none">Tiktok</p>
              </div>
            </div>
            <Button variant="outline" disabled>
              Coming soon
            </Button>
          </div>
          <div className="flex items-center justify-between space-x-4 p-4 border rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-6 h-6">
                <Icons.linkedin />
              </div>
              <div>
                <p className="text-sm font-medium leading-none">LinkedIn</p>
              </div>
            </div>
            <Button variant="outline" disabled>
              Coming soon
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
